// client/src/coms/views/games_parts/GameSessionView.js
// One hosted game: lobby with seats, ready checks and the shared invitation code, the live board, and the
// result with rematch.
import React, { Suspense, lazy, useCallback, useState } from 'react';
import GameRuntimeFrame from './GameRuntimeFrame';
import UserAvatar from './UserAvatar';
import { InviteBox, InviteFriends } from './LobbyParts';

// Client-hosted arcade games load their runtime (and each game) only when a match starts.
const ArcadeStage = lazy(() => import('./arcade/ArcadeStage'));
// Texas Hold'em (Pip's table) loads with its first hand.
const PokerTable = lazy(() => import('./poker/PokerTable'));

const RESULT_TEXT = {
  completed: 'Game over',
  player_left: 'Game over: a player left',
  abandoned: 'Game abandoned',
  ended_by_host: 'The host ended the game',
  host_left: 'The match ended: the host left or lost connection',
};

function ArcadeResult({ final }) {
  if (!final || !final.lines || !final.lines.length) return null;
  return (
    <ul data-testid="arcade-result-lines" style={{ margin: '6px 0 0', paddingLeft: 0, listStyle: 'none', fontSize: 13, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {final.lines.map((line) => <li key={line}>{line}</li>)}
    </ul>
  );
}

function SeatRow({ seat, session, currentUserId, isHost, activeSeat, onKick, busy }) {
  const winner = session.result && session.result.winnerUserIds.includes(seat.userId);
  return (
    <div
      data-testid={`game-seat-${seat.userId}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        padding: '7px 9px',
        borderRadius: 10,
        background: activeSeat ? 'rgba(var(--vx-accent-rgb, 88,101,242), 0.22)' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
        border: activeSeat ? '1px solid rgba(var(--vx-accent-rgb, 88,101,242), 0.55)' : '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))',
        opacity: seat.connected ? 1 : 0.55,
      }}
    >
      <UserAvatar userId={seat.userId} name={seat.name} size={28} />
      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {seat.name}{seat.userId === currentUserId ? <span style={{ opacity: 0.55 }}> (you)</span> : null}
        </div>
        <div style={{ fontSize: 11, opacity: 0.65 }}>
          {seat.userId === session.hostUserId ? 'Host · ' : ''}
          {!seat.connected ? (seat.away ? 'Away' : 'Reconnecting…') : activeSeat ? 'Their turn' : session.status === 'lobby' ? (seat.ready ? 'Ready' : 'Not ready') : `Seat ${seat.index + 1}`}
        </div>
      </div>
      {winner ? <span title="Winner" style={{ fontSize: 16 }}>🏆</span> : null}
      {isHost && session.status !== 'playing' && seat.userId !== currentUserId ? (
        <button type="button" className="coms-mini-btn tiny" disabled={busy} onClick={() => onKick(seat.userId)} aria-label={`Remove ${seat.name}`}>Remove</button>
      ) : null}
    </div>
  );
}

function SeatChip({ seat, session, currentUserId, activeSeat }) {
  const winner = session.result && session.result.winnerUserIds.includes(seat.userId);
  return (
    <div
      data-testid={`game-seat-${seat.userId}`}
      title={seat.connected ? seat.name : `${seat.name} (${seat.away ? 'away' : 'reconnecting'})`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 10px 4px 4px',
        borderRadius: 999,
        background: activeSeat ? 'rgba(var(--vx-accent-rgb, 88,101,242), 0.3)' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))',
        border: activeSeat ? '1px solid rgba(var(--vx-accent-rgb, 88,101,242), 0.7)' : '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
        opacity: seat.connected ? 1 : 0.55,
        fontSize: 12,
        fontWeight: 800,
        transition: 'background 200ms ease, border-color 200ms ease',
      }}
    >
      <UserAvatar userId={seat.userId} name={seat.name} size={22} />
      <span style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {seat.name}{seat.userId === currentUserId ? ' (you)' : ''}
      </span>
      {winner ? <span aria-label="Winner">🏆</span> : null}
      {!seat.connected ? <span style={{ fontSize: 10, opacity: 0.85 }}>{seat.away ? 'Away' : 'Reconnecting…'}</span> : activeSeat ? <span style={{ fontSize: 10, opacity: 0.85 }}>Turn</span> : null}
    </div>
  );
}

// `onBack` returns to the Games catalogue with the game still running; `onLeft` runs after leaving it.
export default function GameSessionView({ game, currentUserId, friends = [], onBack, onLeft }) {
  const { session, act, pending, error, setError } = game;
  const [inviting, setInviting] = useState(false);
  const isHost = Boolean(session && session.hostUserId === currentUserId);
  const seated = Boolean(session && session.seatIndex >= 0);
  const busy = Boolean(pending);
  const connectedCount = session ? session.seats.filter((seat) => seat.connected).length : 0;
  const notReady = session ? session.seats.filter((seat) => seat.userId !== session.hostUserId && seat.ready === false) : [];
  const mySeat = session && seated ? session.seats[session.seatIndex] : null;
  const canStart = session && session.seats.length >= session.minPlayers && connectedCount === session.seats.length && (session.status !== 'lobby' || !notReady.length);
  const currentSeat = session && session.state && Number.isInteger(session.state.currentPlayer) ? session.state.currentPlayer : -1;

  const onMove = useCallback((data) => {
    act('move', { data, gameNumber: session && session.gameNumber });
  }, [act, session]);
  const arcade = Boolean(session && session.state && session.state.arcade);
  const poker = Boolean(session && session.state && session.state.holdem);
  const arcadeFinal = arcade && session.state.final ? session.state.final : null;
  const onRestart = useCallback(() => {
    if (isHost && session && session.status === 'finished') act('rematch');
  }, [act, isHost, session]);
  const onRuntimeError = useCallback((message) => {
    if (message === 'missing_renderer') setError('This game cannot be displayed in your browser.');
  }, [setError]);

  const invite = async (userId) => {
    setInviting(true);
    const response = await fetch(`/api/games/sessions/${encodeURIComponent(session.id)}/invite`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userIds: [Number(userId)] }),
    }).catch(() => null);
    setInviting(false);
    setError(response && response.ok ? '' : 'Could not send the invite.');
  };

  if (!session) {
    return <div style={{ padding: 24, opacity: 0.7 }}>{game.closed ? 'This game is no longer available.' : 'Loading game…'}</div>;
  }

  const statusLabel = session.status === 'lobby' ? 'Waiting for players' : session.status === 'playing' ? `Game ${session.gameNumber} in progress` : 'Finished';
  const showBoard = seated && session.state && session.status !== 'lobby';
  const result = session.result;
  const iWon = result && result.winnerUserIds.includes(currentUserId);

  return (
    <div data-testid="game-session" data-status={session.status} style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))', flexWrap: 'wrap' }}>
        <button type="button" className="coms-mini-btn" onClick={onBack}>← Activities</button>
        <div style={{ flex: '1 1 200px', minWidth: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 900 }}>{session.gameName}</div>
          <div style={{ fontSize: 12, opacity: 0.7 }}>{statusLabel} · {session.seats.length}/{session.maxPlayers} players · {session.visibility === 'server' ? 'Server game' : session.visibility === 'invite' ? 'Invite only' : 'Friends'}</div>
        </div>
        {seated ? (
          <button
            type="button"
            className="coms-mini-btn"
            disabled={busy}
            onClick={async () => {
              const left = await act('leave');
              if (left && left.ok) (onLeft || onBack)();
            }}
          >
            Leave game
          </button>
        ) : session.status !== 'playing' ? (
          <button type="button" className="coms-mini-btn" disabled={busy} onClick={() => act('join')} style={{ background: 'var(--vx-accent, #5865f2)', borderColor: 'var(--vx-accent, #5865f2)' }}>Join game</button>
        ) : null}
      </div>

      {error ? (
        <div role="alert" data-testid="game-error" style={{ margin: '10px 16px 0', padding: '8px 12px', borderRadius: 10, background: 'rgba(237,66,69,0.18)', border: '1px solid rgba(237,66,69,0.45)', fontSize: 13, fontWeight: 700 }}>{error}</div>
      ) : null}

      {result && session.status === 'finished' ? (
        <div data-testid="game-result" style={{ margin: '10px 16px 0', padding: '12px 14px', borderRadius: 12, background: iWon ? 'rgba(59,165,93,0.2)' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))', border: `1px solid ${iWon ? 'rgba(59,165,93,0.5)' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.1 * var(--vx-ov, 1)))'}`, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 220px' }}>
            <div style={{ fontSize: 16, fontWeight: 900 }}>
              {arcadeFinal && arcadeFinal.coop ? (arcadeFinal.won ? 'Victory together!' : 'Defeated together') : result.draw ? "It's a draw!" : iWon ? 'You won!' : result.winnerUserIds.length ? `${result.players.filter((p) => result.winnerUserIds.includes(p.userId)).map((p) => p.name).join(' & ')} won` : RESULT_TEXT[result.reason] || 'Game over'}
            </div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>{RESULT_TEXT[result.reason] || ''}</div>
            <ArcadeResult final={arcadeFinal} />
          </div>
          {isHost ? (
            <>
              <button type="button" className="coms-mini-btn" disabled={busy || !canStart} onClick={() => act('rematch')} style={{ background: 'var(--vx-accent, #5865f2)', borderColor: 'var(--vx-accent, #5865f2)' }}>Rematch</button>
              <button type="button" className="coms-mini-btn" disabled={busy} onClick={() => act('lobby')}>Back to lobby</button>
            </>
          ) : seated ? <div style={{ fontSize: 12, opacity: 0.7 }}>Waiting for the host to start a rematch…</div> : null}
        </div>
      ) : null}

      {showBoard ? (
        <div style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 16px 16px', overflow: 'auto' }}>
          <div data-testid="game-seat-strip" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Arcade games show their own player strip with live scores. */}
            {arcade || poker ? null : session.seats.map((seat) => (
              <SeatChip key={seat.userId} seat={seat} session={session} currentUserId={currentUserId} activeSeat={session.status === 'playing' && seat.index === currentSeat} />
            ))}
            {isHost && session.status === 'playing' ? (
              <button type="button" className="coms-mini-btn" disabled={busy} onClick={() => act('end')} style={{ marginLeft: 'auto', borderColor: 'rgba(237,66,69,0.6)' }}>End game</button>
            ) : null}
          </div>
          {/* The renderers size themselves to the frame, so give the board most of the screen height. */}
          <div style={{ flex: '1 0 auto', height: 'max(460px, calc(100vh - 250px))' }}>
            {poker ? (
              <Suspense fallback={<div style={{ padding: 24, opacity: 0.7 }}>Dealing…</div>}>
                <PokerTable session={session} currentUserId={currentUserId} busy={pending === 'move'} onAct={(data) => act('move', { data, gameNumber: session.gameNumber })} />
              </Suspense>
            ) : arcade ? (
              session.status === 'playing' ? (
                <Suspense fallback={<div style={{ padding: 24, opacity: 0.7 }}>Loading game…</div>}>
                  <ArcadeStage
                    key={`${session.id}-${session.gameNumber}`}
                    session={session}
                    currentUserId={currentUserId}
                    sendSignal={game.sendSignal}
                    onSignal={game.onSignal}
                    getRelayToken={game.getRelayToken}
                    onResult={(data) => act('move', { data, gameNumber: session.gameNumber })}
                  />
                </Suspense>
              ) : (
                <div data-testid="arcade-finished" style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 12, background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))', fontSize: 14, opacity: 0.75, textAlign: 'center', padding: 24 }}>
                  {isHost ? 'Start a rematch or go back to the lobby.' : 'Waiting for the host to start a rematch…'}
                </div>
              )
            ) : (
              <GameRuntimeFrame
                key={`${session.id}-${session.gameNumber}`}
                session={session}
                onMove={onMove}
                onRestart={onRestart}
                onRuntimeError={onRuntimeError}
              />
            )}
          </div>
          {session.feed.length ? (
            <div data-testid="game-feed" style={{ fontSize: 12, opacity: 0.7, display: 'flex', flexWrap: 'wrap', gap: '2px 14px' }}>
              {session.feed.slice(-3).map((entry) => <span key={entry.id}>{entry.text}</span>)}
            </div>
          ) : null}
        </div>
      ) : null}

      {!showBoard ? (
      <div style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', gap: 14, padding: 16, overflow: 'auto' }}>
        <aside style={{ flex: '1 1 auto', maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 900, letterSpacing: 0.6, textTransform: 'uppercase', opacity: 0.6 }}>Players</div>
          {session.seats.map((seat) => (
            <SeatRow
              key={seat.userId}
              seat={seat}
              session={session}
              currentUserId={currentUserId}
              isHost={isHost}
              activeSeat={session.status === 'playing' && seat.index === currentSeat}
              busy={busy}
              onKick={(userId) => act('kick', { userId })}
            />
          ))}

          {session.status === 'lobby' && seated ? <InviteBox code={session.code} compact /> : null}

          {session.status === 'lobby' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
              {!isHost && mySeat ? (
                <button type="button" className={`party-btn${mySeat.ready ? ' ghost' : ''}`} disabled={busy} onClick={() => act('ready', { ready: !mySeat.ready })} data-testid="game-ready-toggle">
                  {mySeat.ready ? 'Not ready' : "I'm ready!"}
                </button>
              ) : null}
              {isHost ? (
                <button type="button" className="coms-mini-btn" disabled={busy || !canStart} onClick={() => act('start')} style={{ padding: '9px 12px', fontSize: 13, fontWeight: 800, background: canStart ? 'var(--vx-accent, #5865f2)' : undefined, borderColor: canStart ? 'var(--vx-accent, #5865f2)' : undefined }}>
                  Start game
                </button>
              ) : seated ? <div style={{ fontSize: 12, opacity: 0.7 }}>Waiting for the host to start.</div> : null}
              {session.seats.length < session.minPlayers ? (
                <div style={{ fontSize: 12, opacity: 0.7 }}>Needs at least {session.minPlayers} players.</div>
              ) : connectedCount < session.seats.length ? (
                <div style={{ fontSize: 12, opacity: 0.7 }}>Waiting for everyone to reconnect.</div>
              ) : notReady.length ? (
                <div style={{ fontSize: 12, opacity: 0.7 }}>Waiting for {notReady.map((seat) => seat.name).join(', ')} to be ready.</div>
              ) : null}
            </div>
          ) : null}

          {isHost && session.status !== 'playing' ? (
            <InviteFriends friends={friends} memberIds={session.seats.map((seat) => seat.userId)} busy={inviting || busy} onInvite={invite} />
          ) : null}

          {session.feed.length ? (
            <div data-testid="game-feed" style={{ marginTop: 6, fontSize: 12, opacity: 0.75, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {session.feed.slice(-6).map((entry) => <div key={entry.id}>{entry.text}</div>)}
            </div>
          ) : null}
        </aside>
      </div>
      ) : null}
    </div>
  );
}
